/**
 * useTier Hook - React hook for tier checking and feature gating
 *
 * Provides a clean interface for components to check tier access,
 * feature availability, and usage limits.
 *
 * Usage:
 *   const { tier, isPro, hasFeature, checkLimit } = useTier();
 *
 *   if (hasFeature('aiAssistant')) {
 *     // Render AI assistant
 *   }
 *
 *   const { allowed, remaining } = checkLimit('recordings', currentRecordingCount);
 *   if (!allowed) {
 *     // Show upgrade prompt
 *   }
 */

import { useAuth } from '../context/AuthContext';
import {
  TIERS,
  TIER_LIMITS,
  hasFeature as checkHasFeature,
  canUseFeature,
  checkLimit as checkTierLimit,
  getUpgradeMessage,
  getTierDisplayName,
  getTierPricing,
  isPremiumTier,
  getFeaturesForTier,
  migrateSubscriptionStatus
} from '../utils/tierUtils';

export const useTier = () => {
  const { userProfile, user } = useAuth();

  // Determine user's tier with fallback logic
  let tier = TIERS.FREE;

  if (userProfile?.subscriptionTier) {
    // New 3-tier system
    tier = userProfile.subscriptionTier;
  } else if (userProfile?.subscriptionStatus) {
    // Legacy 2-tier system - migrate on the fly
    tier = migrateSubscriptionStatus(userProfile.subscriptionStatus);
  }

  // Tier boolean flags
  const isFree = tier === TIERS.FREE;
  const isPro = tier === TIERS.PRO;
  const isTeam = tier === TIERS.TEAM;
  const isPremium = isPremiumTier(tier); // Pro or Team

  // Organization context
  const hasOrganization = !!userProfile?.organizationId;
  const organizationRole = userProfile?.organizationRole || null;
  const isOrgOwner = organizationRole === 'owner';
  const isOrgAdmin = organizationRole === 'admin' || organizationRole === 'owner';
  const isOrgMember = !!organizationRole;

  /**
   * Check if the current user has access to a feature
   * @param {string} feature - Feature name
   * @returns {boolean} Whether user can access the feature
   */
  const hasFeature = (feature) => {
    return canUseFeature(userProfile, feature);
  };

  /**
   * Check if current usage is within limits
   * @param {string} limitType - Type of limit to check
   * @param {number} currentUsage - Current usage count
   * @returns {object} { allowed: boolean, remaining: number, limit: number }
   */
  const checkLimit = (limitType, currentUsage = 0) => {
    return checkTierLimit(userProfile, limitType, currentUsage);
  };

  /**
   * Get upgrade message for a feature
   * @param {string} feature - Feature name
   * @returns {object} { requiredTier: string, message: string }
   */
  const getUpgradeInfo = (feature) => {
    return getUpgradeMessage(tier, feature);
  };

  /**
   * Get the limit value for a specific limit type
   * @param {string} limitType - Type of limit
   * @returns {number} Limit value (-1 for unlimited, 0 for disabled)
   */
  const getLimit = (limitType) => {
    return TIER_LIMITS[tier]?.[limitType] ?? 0;
  };

  /**
   * Get display name for current tier
   * @returns {string} Tier display name
   */
  const getTierName = () => {
    return getTierDisplayName(tier);
  };

  /**
   * Get pricing information for current or specified tier
   * @param {string} targetTier - Optional tier to get pricing for
   * @returns {object} Pricing information
   */
  const getPricing = (targetTier = null) => {
    return getTierPricing(targetTier || tier);
  };

  /**
   * Get all available features for current tier
   * @returns {string[]} Array of feature names
   */
  const getAvailableFeatures = () => {
    return getFeaturesForTier(tier);
  };

  /**
   * Check if a specific feature requires an upgrade
   * @param {string} feature - Feature name
   * @returns {boolean} Whether feature requires upgrade
   */
  const requiresUpgrade = (feature) => {
    return !hasFeature(feature);
  };

  /**
   * Get usage data from user profile
   * @returns {object} Usage data
   */
  const getUsage = () => {
    return userProfile?.usage || {
      scheduledMeetingsCount: 0,
      recordingsCount: 0,
      storageUsed: 0,
      activePollsCount: 0,
      meetingNotesCount: 0,
    };
  };

  /**
   * Check if user can perform an action based on current usage
   * @param {string} limitType - Type of limit to check
   * @param {number} currentUsage - Optional current usage override
   * @returns {boolean} Whether action is allowed
   */
  const canPerformAction = (limitType, currentUsage = null) => {
    const usage = currentUsage ?? getUsage()[`${limitType}Count`] ?? 0;
    const { allowed } = checkLimit(limitType, usage);
    return allowed;
  };

  return {
    // Tier information
    tier,
    tierName: getTierName(),

    // Tier flags
    isFree,
    isPro,
    isTeam,
    isPremium,

    // Organization context
    hasOrganization,
    organizationId: userProfile?.organizationId || null,
    organizationRole,
    isOrgOwner,
    isOrgAdmin,
    isOrgMember,

    // Feature checking
    hasFeature,
    requiresUpgrade,
    getUpgradeInfo,

    // Limit checking
    checkLimit,
    getLimit,
    canPerformAction,

    // Usage data
    getUsage,
    usage: getUsage(),

    // Tier information getters
    getPricing,
    getAvailableFeatures,

    // Constants for component use
    TIERS,
    TIER_LIMITS: TIER_LIMITS[tier],

    // User context
    userId: user?.uid || null,
    userProfile,
  };
};

export default useTier;
